import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReferralTable = ({ referrals, sortConfig, onSort, onViewDetails, onViewAISummary, onStatusUpdate, onDelete, onUrgencyUpdate, onGenerateReport }) => {
  const [urgencyDropdownOpen, setUrgencyDropdownOpen] = React.useState(null);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setUrgencyDropdownOpen(null);
      }
    };

    if (urgencyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [urgencyDropdownOpen]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: 'Nouveau', color: 'bg-primary/20 text-primary border-primary/40' },
      reviewed: { label: 'En revue', color: 'bg-warning/20 text-warning border-warning/40' },
      scheduled: { label: 'Programmé', color: 'bg-success/20 text-success border-success/40' },
      completed: { label: 'Terminé', color: 'bg-muted/20 text-muted-foreground border-muted/40' },
      cancelled: { label: 'Annulé', color: 'bg-error/20 text-error border-error/40' }
    };

    const config = statusConfig?.[status] || statusConfig?.new;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getUrgencyBadge = (urgency, urgencySummary) => {
    const urgencyConfig = {
      routine: { label: 'Routine', icon: 'Circle', color: 'text-muted-foreground', bgColor: 'bg-muted/10', borderColor: 'border-muted/20' },
      urgent: { label: 'Urgent', icon: 'AlertCircle', color: 'text-warning', bgColor: 'bg-warning/10', borderColor: 'border-warning/30' },
      emergency: { label: 'Urgence', icon: 'AlertTriangle', color: 'text-error', bgColor: 'bg-error/10', borderColor: 'border-error/30' }
    };

    const config = urgencyConfig?.[urgency] || urgencyConfig?.routine;
    return (
      <div className="flex items-center gap-1">
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md ${config?.bgColor} border ${config?.borderColor}`}>
          <Icon name={config?.icon} size={12} className={config?.color} />
          <span className={`text-[10px] font-medium ${config?.color}`}>{config?.label}</span>
        </div>
        {urgencySummary && (
          <div className="group relative">
            <Icon name="Sparkles" size={10} className="text-purple-400/70" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-card-elevated border border-white/10 rounded text-[10px] text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              Classifié par ChatGPT
            </div>
          </div>
        )}
      </div>
    );
  };

  const getSortIcon = (field) => {
    if (sortConfig?.field !== field) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' ? (
      <Icon name="ChevronUp" size={16} className="text-primary" />
    ) : (
      <Icon name="ChevronDown" size={16} className="text-primary" />
    );
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm', { locale: fr });
    } catch {
      return '-';
    }
  };

  const getPatientInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`;
  };

  if (referrals?.length === 0) {
    return (
      <div className="bg-card-elevated border border-white/8 p-12 rounded-xl text-center shadow-lg">
        <Icon name="Inbox" size={48} className="text-muted mx-auto mb-4" />
        <p className="text-lg text-secondary">Aucun adressage trouvé</p>
        <p className="text-sm text-muted mt-2">Essayez de modifier vos filtres</p>
      </div>
    );
  }

  return (
    <div className="bg-card-elevated border border-white/8 rounded-xl overflow-hidden shadow-xl">
      <div className="overflow-x-auto">
        <table className="w-full min-w-full table-fixed">
          <thead className="bg-card border-b border-white/8">
            <tr>
              <th className="w-[12%] px-3 py-4 text-left">
                <button
                  onClick={() => onSort('submissionDate')}
                  className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-accent transition-colors"
                >
                  Date
                  {getSortIcon('submissionDate')}
                </button>
              </th>
              <th className="w-[18%] px-3 py-4 text-left">
                <button
                  onClick={() => onSort('patientLastName')}
                  className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-accent transition-colors"
                >
                  Patient
                  {getSortIcon('patientLastName')}
                </button>
              </th>
              <th className="w-[16%] px-3 py-4 text-left">
                <button
                  onClick={() => onSort('referringDentistName')}
                  className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-accent transition-colors"
                >
                  Dentiste
                  {getSortIcon('referringDentistName')}
                </button>
              </th>
              <th className="w-[10%] px-3 py-4 text-left text-xs font-semibold text-primary">
                Dents
              </th>
              <th className="w-[12%] px-3 py-4 text-left">
                <button
                  onClick={() => onSort('urgency')}
                  className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-accent transition-colors"
                >
                  Urgence
                  {getSortIcon('urgency')}
                </button>
              </th>
              <th className="w-[12%] px-3 py-4 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-accent transition-colors"
                >
                  Statut
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="w-[20%] px-3 py-4 text-right text-xs font-semibold text-primary">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {referrals?.map((referral) => (
              <tr
                key={referral?.id}
                className="hover:bg-card-hover transition-colors"
              >
                <td className="px-3 py-3">
                  <div className="text-xs text-secondary truncate">
                    {formatDate(referral?.submissionDate)}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center border border-accent/30 flex-shrink-0">
                      <span className="text-xs font-semibold text-accent">
                        {getPatientInitials(referral?.patientFirstName, referral?.patientLastName)}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-foreground truncate">
                        {referral?.patientFirstName} {referral?.patientLastName}
                      </div>
                      {referral?.patientYearOfBirth && (
                        <div className="text-[10px] text-muted-foreground truncate">
                          {referral?.patientYearOfBirth}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-xs text-foreground truncate" title={referral?.referringDentistName}>
                    {referral?.referringDentistName}
                  </div>
                  {referral?.referringPracticeName && (
                    <div className="text-[10px] text-muted-foreground truncate" title={referral?.referringPracticeName}>
                      {referral?.referringPracticeName}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1">
                    {referral?.toothNumbers?.slice(0, 2)?.map((tooth, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-accent/20 text-accent text-[10px] font-medium"
                      >
                        {tooth}
                      </span>
                    ))}
                    {referral?.toothNumbers?.length > 2 && (
                      <span className="text-[10px] text-muted-foreground">+{referral?.toothNumbers?.length - 2}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3">
                  {getUrgencyBadge(referral?.urgency, referral?.urgency_summary)}
                </td>
                <td className="px-3 py-3">
                  {getStatusBadge(referral?.status)}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {onGenerateReport && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => onGenerateReport(referral)}
                        iconName="FileText"
                        className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] px-2 py-1"
                        title="Générer un compte rendu pour ce patient"
                      >
                        Générer CR
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(referral)}
                      iconName="Eye"
                      className="text-[10px] px-2 py-1"
                    >
                      Détails
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewAISummary(referral)}
                      iconName="Sparkles"
                      className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 text-[10px] px-2 py-1"
                      title={referral?.urgency_summary ? "Voir le résumé IA" : "Générer un résumé IA"}
                    >
                      IA
                    </Button>
                    {referral?.status === 'new' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onStatusUpdate(referral?.id, 'reviewed')}
                        iconName="CheckCircle"
                        className="text-[10px] px-2 py-1"
                      >
                        Revu
                      </Button>
                    )}
                    {onUrgencyUpdate && (
                      <div className="relative" ref={urgencyDropdownOpen === referral?.id ? dropdownRef : null}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setUrgencyDropdownOpen(urgencyDropdownOpen === referral?.id ? null : referral?.id)}
                          iconName="AlertCircle"
                          className={`text-[10px] px-1.5 py-1 ${
                            referral?.urgency === 'urgent' ? 'text-warning hover:text-warning hover:bg-warning/10' :
                            referral?.urgency === 'emergency'? 'text-error hover:text-error hover:bg-error/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'
                          }`}
                          title="Modifier l'urgence"
                        />
                        {urgencyDropdownOpen === referral?.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-card-elevated border border-white/10 rounded-lg shadow-xl z-50">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  onUrgencyUpdate(referral?.id, 'routine');
                                  setUrgencyDropdownOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-muted-foreground hover:bg-card-hover hover:text-foreground flex items-center gap-2 transition-colors"
                              >
                                <Icon name="Circle" size={14} />
                                Routine
                              </button>
                              <button
                                onClick={() => {
                                  onUrgencyUpdate(referral?.id, 'urgent');
                                  setUrgencyDropdownOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-warning hover:bg-warning/10 flex items-center gap-2 transition-colors"
                              >
                                <Icon name="AlertCircle" size={14} />
                                Urgent
                              </button>
                              <button
                                onClick={() => {
                                  onUrgencyUpdate(referral?.id, 'emergency');
                                  setUrgencyDropdownOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error/10 flex items-center gap-2 transition-colors"
                              >
                                <Icon name="AlertTriangle" size={14} />
                                Urgence
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(referral?.id)}
                        iconName="Trash2"
                        className="text-error hover:text-error hover:bg-error/10 text-[10px] px-1.5 py-1"
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReferralTable;